/*
Description: Injector for Comprehend's own lead-capture pages (conference booth form)
Date: 10/07/2026
Author: ComprehendAI Team

Notes:
    All Injectors need the following functions:
        getQuestions
        answerQuestions
        answerQuestion
        getPageInfo
        getPatientInfo
        getLogFromInjector

Lead Capture Specific Notes:
    We own the page, so instead of scraping an EMR's layout the page declares
    its sections. Every input/textarea/select carrying data-cpt-field is a
    section, keyed by that attribute's value:
        data-cpt-field="Pain points"     section key (becomes the template section)
        data-cpt-type="Short Answer"     optional; defaults by element
                                         (select → Multiple Choice,
                                          input → Short Answer,
                                          textarea → Long Answer)
        data-cpt-own-section             Flex mode: keep this field its own
                                         section (the transcript, which needs
                                         its own output budget)
        data-cpt-hint="..."              Flex mode: the field's AI description
    With data-cpt-flex="<name>" on the form, all other fields become one Flex
    section of that name, with subsections from each field's card heading
    (<section><h2>). Without it, each field is its own section.
    The page's <form data-cpt-note-type="..."> sets the Note Type. Page info
    is a fixed canonical URL and patient info is null, so the EMR session
    fingerprint never changes mid-Comprehend and no patient is created.
*/

const DEFAULT_NOTE_TYPE = 'Conference Lead Capture';
// Reported instead of the page's real address. The booth template is then one
// template wherever the page is hosted, and a localhost URL in the template
// request body trips the API WAF's EC2MetaDataSSRF_BODY rule (blocked outright).
const CANONICAL_URL = 'https://comprehend-booth-capture';

function getForm(): HTMLElement | null {
  return document.querySelector('[data-cpt-note-type]') as HTMLElement | null;
}

/** Main Functions */
export function getPageInfo() {
  shareLoggedInRep();
  const form = getForm();
  return {
    URL: CANONICAL_URL,
    'Note Type':
      form?.getAttribute('data-cpt-note-type')?.trim() || DEFAULT_NOTE_TYPE
  };
}

// A prospect isn't a patient. Reporting none makes New Visit create a visit
// without one (rather than a nameless patient per lead), and keeps the
// session fingerprint constant while Comprehend fills the form.
export function getPatientInfo() {
  return null;
}

function setNativeValue(
  el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  value: string
) {
  const proto =
    el instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : el instanceof HTMLSelectElement
        ? window.HTMLSelectElement.prototype
        : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function defaultType(el: Element): string {
  if (el instanceof HTMLSelectElement) return 'Multiple Choice';
  if (el instanceof HTMLTextAreaElement) return 'Long Answer';
  return 'Short Answer';
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

function getExistingNote(value: any) {
  if (typeof value !== 'string') return null;
  const val = value.trim();
  return val === '' ? null : val;
}

// Global state
var questions = [];
var questionInteractions = {};

// Answers that mean "not in the conversation"; never written to the page.
const NOT_DISCUSSED = ['not discussed', 'none', 'n/a', '-99999'];

function isNotDiscussed(value) {
  return (
    isEmptyOrNull(value) ||
    NOT_DISCUSSED.includes(String(value).trim().toLowerCase())
  );
}

// Writes one answer into one page field: text into inputs/textareas, a
// matching option (case-insensitive) into selects.
function fieldWriter(
  el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
) {
  if (el instanceof HTMLSelectElement) {
    const choices = {};
    Array.from(el.options).forEach((opt) => {
      if (!opt.value) return;
      choices[opt.text.trim()] = () => {
        setNativeValue(el, opt.value);
        el.classList.add('cpt-filled');
      };
    });
    return { choices, write: null };
  }
  return {
    choices: null,
    write: (val: any, replyType: string) => {
      if (isNotDiscussed(val)) return;
      const text = String(val);
      if (replyType === 'append' && el.value) {
        setNativeValue(el, el.value + '\n' + text);
      } else {
        setNativeValue(el, text);
      }
      el.classList.add('cpt-filled');
    }
  };
}

function currentValue(
  el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
) {
  if (el instanceof HTMLSelectElement) {
    return el.selectedIndex > 0 ? el.options[el.selectedIndex].text.trim() : '';
  }
  return el.value || '';
}

export function getQuestions() {
  questions = [];
  questionInteractions = {};
  const form = getForm();
  // data-cpt-flex on the form turns every field (except data-cpt-own-section
  // ones) into one Flex section, grouped into subsections by the card heading
  // each field sits under. One structured AI call fills them together (so a multi-person
  // conversation stays on one person), Learn can refine it from rep edits,
  // and each field's data-cpt-hint becomes its schema Description.
  const flexKey = form?.getAttribute('data-cpt-flex')?.trim();
  const flex: any = flexKey
    ? { Key: flexKey, Question: flexKey, Type: 'Flex', Structure: {} }
    : null;
  const flexInputs: Record<string, Record<string, any>> = {};

  const fields = document.querySelectorAll('[data-cpt-field]');
  fields.forEach((node) => {
    const el = node as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const label = el.getAttribute('data-cpt-field')?.trim();
    if (!label) return;
    const ownSection = el.hasAttribute('data-cpt-own-section');
    const { choices, write } = fieldWriter(el);

    if (flex && !ownSection) {
      const subsection =
        el.closest('section')?.querySelector('h2')?.textContent?.trim() ||
        flexKey;
      const leaf: any = { 'Existing Value': currentValue(el) };
      const hint = el.getAttribute('data-cpt-hint');
      if (hint) leaf.Description = hint;
      if (choices) leaf.Choices = Object.keys(choices);
      flex.Structure[subsection] = flex.Structure[subsection] || {};
      flex.Structure[subsection][label] = leaf;
      flexInputs[subsection] = flexInputs[subsection] || {};
      flexInputs[subsection][label] = { choices, write };
      return;
    }

    const type = el.getAttribute('data-cpt-type') || defaultType(el);
    const existingNote = getExistingNote(el.value);
    const questionObj: any = {
      Key: label,
      Question: label,
      Type: type,
      ExistingNote: existingNote
    };
    if (choices) {
      questionObj.Choices = Object.keys(choices);
      questionInteractions[label] = { Type: type, Choices: choices };
    } else {
      questionInteractions[label] = {
        Type: type,
        ExistingNote: existingNote,
        Input: write
      };
    }
    questions.push(questionObj);
  });

  if (flex && Object.keys(flex.Structure).length) {
    questionInteractions[flexKey] = { Type: 'Flex', Inputs: flexInputs };
    // Flex goes first so the transcript still lands last.
    questions.unshift(flex);
  }
  return questions;
}

function parseFlexAnswer(answer) {
  if (typeof answer !== 'string') return answer || {};
  try {
    return JSON.parse(answer);
  } catch (e) {
    return {};
  }
}

function answerFlex(interaction, answer, replyType) {
  const errors = [];
  const parsed = parseFlexAnswer(answer);
  Object.entries(parsed).forEach(([subsection, values]) => {
    const inputs = interaction.Inputs[subsection];
    if (!inputs || !values || typeof values !== 'object') return;
    Object.entries(values as Record<string, any>).forEach(([label, value]) => {
      const target = inputs[label];
      if (!target || isNotDiscussed(value)) return;
      if (target.write) {
        target.write(value, replyType);
        return;
      }
      const pick = (Array.isArray(value) ? value : [value])
        .map((v) => findChoice(target.choices, v))
        .find(Boolean);
      if (pick) pick();
      else errors.push(`${label}: no choice matches "${value}"`);
    });
  });
  return errors.length ? errors.join('; ') : true;
}

export function answerQuestions(answers) {
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error !== true) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  return errors;
}

function findChoice(choices: Record<string, Function>, answer: any) {
  if (isEmptyOrNull(answer)) return null;
  const wanted = String(answer).trim();
  if (wanted in choices) return choices[wanted];
  const lower = wanted.toLowerCase();
  const key = Object.keys(choices).find((c) => c.toLowerCase() === lower);
  return key ? choices[key] : null;
}

export function answerQuestion(question, answer, replyType) {
  const interaction = questionInteractions[question];
  if (interaction == null) {
    console.error('Question not found', question);
    return 'Question not found';
  }
  if (interaction.Type === 'Flex') {
    return answerFlex(interaction, answer, replyType);
  }
  if (interaction.Input) {
    interaction.Input(answer, replyType);
    return true;
  }
  // Multiple Choice (selects). Choose All isn't used on lead forms; take the
  // first match if the model returns a list anyway.
  const candidates = Array.isArray(answer) ? answer : [answer];
  for (const candidate of candidates) {
    const choose = findChoice(interaction.Choices, candidate);
    if (choose) {
      choose();
      return true;
    }
  }
  if (isEmptyOrNull(answer)) return true;
  console.error('Answer not found', question, answer);
  return 'Answer not found';
}

export async function getLogFromInjector() {
  try {
    return { questions, questionInteractions };
  } catch (error) {
    return { error: error?.toString?.() || 'Unknown error' };
  }
}

// The booth page's "Captured by" is whoever is logged into Comprehend: they
// are the one recording. The panel mirrors its session into storage.local
// (index.js PERSISTENT_SESSION_KEY); hand only the email to this page, as a
// form attribute and a 'cpt-rep' event, and again whenever the login changes.
const SESSION_STORAGE_KEY = 'comprehendpt_note_capture_session';

function publishRep(email: string) {
  getForm()?.setAttribute('data-cpt-rep', email);
  window.dispatchEvent(new CustomEvent('cpt-rep', { detail: email }));
}

let sharingRep = false;

// Called from getPageInfo, which the panel requests whenever it opens a visit
// or scans the page; the first call hands over the email and starts following
// login changes.
export function shareLoggedInRep() {
  const storage = (globalThis as any).chrome?.storage?.local;
  if (!storage || sharingRep) return;
  sharingRep = true;
  storage.get(SESSION_STORAGE_KEY, (items: any) => {
    publishRep(items?.[SESSION_STORAGE_KEY]?.sessionEmail || '');
  });
  (globalThis as any).chrome.storage.onChanged?.addListener(
    (changes: any, area: string) => {
      if (area !== 'local' || !changes[SESSION_STORAGE_KEY]) return;
      publishRep(changes[SESSION_STORAGE_KEY].newValue?.sessionEmail || '');
    }
  );
}
